/** Squared distance from point (px,py) to segment (ax,ay)-(bx,by). */
function segDistSq(
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
): number {
  const dx = bx - ax
  const dy = by - ay
  let x = ax
  let y = ay
  const lenSq = dx * dx + dy * dy
  if (lenSq > 0) {
    const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq))
    x = ax + dx * t
    y = ay + dy * t
  }
  const ex = px - x
  const ey = py - y
  return ex * ex + ey * ey
}

/**
 * Ramer-Douglas-Peucker on a flat [x,y,x,y,...] list. Iterative, because a
 * long freehand stroke can be thousands of points and recursion worst-cases to
 * one frame per point.
 */
export function simplifyPoints(points: readonly number[], tolerance: number): number[] {
  const n = points.length / 2
  if (n < 3 || tolerance <= 0) return [...points]

  const tolSq = tolerance * tolerance
  const keep = new Uint8Array(n)
  keep[0] = 1
  keep[n - 1] = 1

  const stack: [number, number][] = [[0, n - 1]]
  while (stack.length > 0) {
    const [first, last] = stack.pop()!
    if (last - first < 2) continue

    const ax = points[first * 2]!
    const ay = points[first * 2 + 1]!
    const bx = points[last * 2]!
    const by = points[last * 2 + 1]!

    let worst = -1
    let worstDist = 0
    for (let i = first + 1; i < last; i++) {
      const d = segDistSq(points[i * 2]!, points[i * 2 + 1]!, ax, ay, bx, by)
      if (d > worstDist) {
        worstDist = d
        worst = i
      }
    }

    if (worstDist > tolSq && worst > 0) {
      keep[worst] = 1
      stack.push([first, worst], [worst, last])
    }
  }

  const out: number[] = []
  for (let i = 0; i < n; i++) {
    if (keep[i]) out.push(points[i * 2]!, points[i * 2 + 1]!)
  }
  return out
}
